import { TContentCreatorFacet, TContentType } from '@/types/Content.types';
import { THealthDomain } from '@/types/Health.types';

// The feed's filter state, applied as one deferred set (the modal edits a draft and only
// commits it on "Apply Filters").
//
// API shape drives multiplicity: /filtered, /search and /personalized-feed all now take
// `type[]`, `domains[]` and `subdomains[]` (multi), but a single `creator`. So Type is
// multi-select like Domain/Topic; Creator stays single (a two-value set would let "both
// selected" look active while sending no filter at all).
export type TContentFilters = {
  types: TContentType[];
  domains: THealthDomain[];
  topics: string[]; // subdomain codes, e.g. SLEEP_PATTERNS
  creator?: TContentCreatorFacet;
};

export const EMPTY_CONTENT_FILTERS: TContentFilters = { types: [], domains: [], topics: [] };

const sameSet = <T>(a: T[], b: T[]): boolean => a.length === b.length && a.every((v) => b.includes(v));

export const filtersEqual = (a: TContentFilters, b: TContentFilters): boolean =>
  a.creator === b.creator && sameSet(a.types, b.types) && sameSet(a.domains, b.domains) && sameSet(a.topics, b.topics);

export const isFiltersActive = (f: TContentFilters): boolean =>
  f.types.length > 0 || Boolean(f.creator) || f.domains.length > 0 || f.topics.length > 0;

// Badge count on the filter button. Creator counts as one; types/domains/topics count per
// selection.
export const countActiveFilters = (f: TContentFilters): number =>
  f.types.length + (f.creator ? 1 : 0) + f.domains.length + f.topics.length;

export const toggleInSet = <T>(list: T[], item: T): T[] =>
  list.includes(item) ? list.filter((v) => v !== item) : [...list, item];

// Single-select toggle: tapping the active value clears it, otherwise it replaces.
export const toggleSingle = <T>(current: T | undefined, value: T): T | undefined =>
  current === value ? undefined : value;
